import {DrupalMedia} from "next-drupal";
import {Fragment, useContext, useState} from "react";
import {Button} from "../atoms/button";
import {Link} from "@/components/atoms/link";
import {DocumentPagination} from "@/components/organisms/widget--document-search";
import classNames from "classnames";
import {Dictionary, DocumentFilter} from "@/types/hygiena-types";
import {FilterResetButton} from "../atoms/reset-button";
import {getDocumentUri} from "@/lib/get-document-uri";
import {DictionaryContext} from "@/context/dictionary-context";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@/components/shadcn/ui/select";
import {useRouter} from "next/router";

/**
 * A resource section component (Document Library), to display documents.
 *
 * @note The categories and industries fields must be included.
 *
 * @param documents
 *   An array of documents.
 * @constructor
 */
export function WidgetResourcesSection ({ documents }: { documents: DrupalMedia[] }) {
  const t = useContext<Dictionary>(DictionaryContext);
  const [page, setPage] = useState(0);
  const [pageLimit, setPageLimit] = useState(10);
  const [category, setCategory] = useState<string>("");
  const [language, setLanguage] = useState<string>("");
  const [region, setRegion] = useState<string>("");
  const filteredDocuments = getCurrentDocuments();
  const currentDocuments = filteredDocuments.slice(page * pageLimit, (page * pageLimit) + pageLimit);
  const documentCount = filteredDocuments?.length ?? 0;
  let availableFilterIds: string[] = [];
  const {categoryFilters, languageFilters, regionFilters} = getDocumentFilters();
  const router = useRouter();

  const docLinkProps = router.locale !== router.defaultLocale
    ? { rel: 'noindex nofollow'}
    : {};

  /**
   * Gets the available document filters.
   */
  function getDocumentFilters(): {
    categoryFilters: DocumentFilter[],
    languageFilters: DocumentFilter[],
    regionFilters: DocumentFilter[],
  }
  {
    const added = {};
    let categoryFilters: DocumentFilter[] = [];
    let languageFilters: DocumentFilter[] = [];
    let regionFilters: DocumentFilter[] = [];
    const types = [
      {name: "field_document_categories", filters: categoryFilters, array: true},
      {name: "field_document_language", filters: languageFilters, array: true},
      {name: "field_document_region", filters: regionFilters, array: true},
    ]
    for (const doc of documents) {
      for (const type of types) {
        if (doc?.[type?.name]) {
          if (type.array) {
            doc[type.name].map(item => {
              if (item?.type === "unknown" || added[item?.id]) return;
              if (!added?.[item?.id]) added[item.id] = true;
              type.filters.push({id: item.id, name: item.name, element_id: item?.field_document_category_id ?? ""});
            });
          }
          else {
            const item = doc[type.name];
            if (!added?.[item?.id]) {
              added[item.id] = true;
              type.filters.push({id: item.id, name: item.name, element_id: item?.field_document_category_id ?? ""});
            }
          }
        }
      }
    }

    categoryFilters = categoryFilters.sort((a, b) =>  a?.name?.localeCompare(b?.name));
    languageFilters = languageFilters.sort((a, b) =>  a?.name?.localeCompare(b?.name));
    regionFilters = regionFilters.sort((a, b) =>  a?.name?.localeCompare(b?.name));
    return {categoryFilters, languageFilters, regionFilters};
  }

  if (filteredDocuments?.length) {
    for (const doc of filteredDocuments) {
      const languageMatch = language && language === doc?.field_document_language?.[0]?.id;
      const regionMatch = region && region === doc?.field_document_region?.[0]?.id;
      const categoryMatch = category && doc?.field_document_categories?.filter(item => item?.id === category)?.length > 0;

      if (language && !languageMatch) continue;
      if (region && !regionMatch) continue;
      if (category && !categoryMatch) continue;

      if (doc?.field_document_language?.[0]?.id && !availableFilterIds.includes(doc.field_document_language[0].id)) {
        availableFilterIds.push(doc.field_document_language[0].id);
      }
      if (doc?.field_document_region?.[0]?.id && !availableFilterIds.includes(doc.field_document_region[0].id)) {
        availableFilterIds.push(doc.field_document_region[0].id);
      }
      if (doc?.field_document_categories) {
        for (const cat of doc.field_document_categories) {
          if (!cat?.id) continue;
          if (!availableFilterIds.includes(cat?.id)) {
            availableFilterIds.push(cat.id);
          }
        }
      }
    }
  }

  /**
   * Returns true if an option is disabled.
   */
  function optionIsDisabled(id: string, type?: string): boolean {
    // If only a single filter is in use, and the current category is of that filter, display all available options.
    const singleFilter = [category, language, region].filter(item => item?.length)?.length < 2;
    if (singleFilter && category && type === "category") return false;
    if (singleFilter && language && type === "language") return false;
    if (singleFilter && region && type === "region") return false;

    if (availableFilterIds?.length === 0) return false;
    return !availableFilterIds?.includes(id);
  }


  function getCurrentDocuments() {
    // Ensure only unique documents are returned.
    const added: (string|number)[] = [];
    if (documents) return documents.filter(item => {
      let match = true;
      if (added.includes(item?.id)) return false;
      if (language?.length) {
        match = item?.field_document_language?.[0]?.id === language;
      }
      if (match && region?.length) {
        match = item?.field_document_region?.[0]?.id === region;
      }
      if (match && category?.length) {
        match = false;
        if (item?.field_document_categories?.length) {
          for (const cat of item?.field_document_categories) {
            if (cat?.id === category) {
              match = true;
              break;
            }
          }
        }
      }
      if (item?.id) {
        added.push(item?.id);
      }
      return match;
    });
    return [];
  }

  /**
   * Displays a red reset filter button.
   *
   * @param type
   *   The filter type.
   */
  function resetFilters(type: "language"|"category"|"region"|"all") {
    return (
      <div onClick={() => {
        if (type === "language") setLanguage("");
        if (type === "category") setCategory("");
        if (type === "region") setRegion("");
        if (type === "all") {
          setLanguage("");
          setCategory("");
          setRegion("");
        }
        setPage(0);
      }} className={classNames("reset-filters ml-0.5 transition-opacity inline-block duration-300", {
        "opacity-0": type === "language" && !language || type === "category" && !category || type === "region" && !region
          || type === "all" && (!language && !category && !region),
      })}>
        <div className="bg-accent-red h-[20px] w-[20px] rounded-md cursor-pointer relative flex justify-center">
          <div className="bg-white h-[16px] w-[2px] rounded-md absolute left-[9px] top-[2px] rotate-45"/>
          <div className="bg-white h-[16px] w-[2px] rounded-md absolute left-[9px] top-[2px] -rotate-45"/>
        </div>
      </div>
    );
  }

  async function changePage(page) {
    await setPage(page);
  }

  return (
    <>
      <div className="paragraph--document-library overflow-auto">
        <div className="bg-white flex flex-col md:flex-row p-6 gap-2 md:gap-6 text-black items-center justify-end mb-4">
          <div className="text-right w-full flex justify-end items-center gap-2 md:hidden">
            Filter documents: {resetFilters("all")}
          </div>
          {categoryFilters?.length ? (
            <label id="document-library--category" htmlFor="document-category" className="font-semibold w-1/5 max-md:w-full">
              {`${t?.documents?.category}`}
              <Select name={"document-category"}
                      value={category}
                      onValueChange={val => {setCategory(val !== "0" ? val : ""); setPage(0)}}>
                <SelectTrigger className={classNames("border-hygienaGray border rounded-sm text-left font-normal w-full bg-white")} >
                  <SelectValue placeholder={`${t?.documents?.all_categories}`} />
                </SelectTrigger>
                <SelectContent>
                  {categoryFilters.map(item => (
                    <SelectItem key={`document-library-filter-category--${item.id}`} value={item.id}
                            id={item?.element_id ? `document-library--category--${item.element_id}` : undefined}
                            disabled={optionIsDisabled(item.id, "category")}>{item.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>
          ): <></>}
          {languageFilters?.length ? (
            <label id={`document-library--language`} htmlFor="document-language" className="font-semibold w-1/5 max-md:w-full">
              {`${t?.documents?.language}`}
              <Select name={"document-language"}
                      value={language}
                      onValueChange={val => {setLanguage(val !== "0" ? val : ""); setPage(0)}}>
                <SelectTrigger className={classNames("border-hygienaGray border rounded-sm text-left font-normal w-full bg-white")} >
                  <SelectValue placeholder={`${t?.documents?.all_languages}`} />
                </SelectTrigger>
                <SelectContent>
                  {languageFilters.map(item => (
                    <SelectItem key={`document-library-filter-language--${item.id}`} value={item.id}
                            id={item?.element_id ? `document-library--language--${item.element_id}` : undefined}
                            disabled={optionIsDisabled(item.id, "language")}>{item.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>
          ): <></>}
          {regionFilters?.length ? (
            <label htmlFor="document-region" className="font-semibold w-1/5 max-md:w-full">
              {`${t?.documents?.region_sds}`}
              <Select name={"document-region"}
                      value={region}
                      onValueChange={val => {setRegion(val !== "0" ? val : ""); setPage(0)}}>
                <SelectTrigger className={classNames("border-hygienaGray border rounded-sm text-left font-normal w-full bg-white")} >
                  <SelectValue placeholder={`${t?.documents?.select}`} />
                </SelectTrigger>
                <SelectContent>
                  {regionFilters.map(item => (
                    <SelectItem key={`document-library-filter-region--${item.id}`} value={item.id}
                                id={item?.element_id ? `document-library--region--${item.element_id}` : undefined}
                                disabled={optionIsDisabled(item.id)}>{item.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>
          ):<></>}
          <div className="flex self-end pb-[6px]">
            <FilterResetButton enabled={(category || language || region)?.length > 0} callback={() => {
              setCategory("");
              setLanguage("");
              setRegion("");
              setPage(0);
            }} className="!ml-0"/>
          </div>
        </div>
        <div className="overflow-auto">
          <table className="w-full table-auto text-black">
            <thead className="text-left text-black bg-white max-md:hidden">
              <tr className="border-b-hygienaLight border-b-4">
                <th className="py-3 px-2 pl-6 lg:min-w-[400px] lg:text-xl font-semibold">{`${t?.documents?.title}`}</th>
                <th className="py-3 px-2 lg:text-xl font-semibold">
                  <div className="flex gap-0.5 items-center">
                    <span>{`${t?.documents?.categories}`}</span>
                  </div>
                </th>
                {languageFilters?.length ? (
                  <th className="py-3 px-2 lg:text-xl font-semibold">
                    <div className="flex gap-0.5 items-center">
                      <span>{`${t?.documents?.language}`}</span>
                    </div>
                  </th>
                ): <></>}
                {regionFilters?.length ? (
                  <th className="py-3 px-2 lg:text-xl font-semibold lg:min-w-[150px]">
                    <div className="flex gap-0.5 items-center">
                      <span>{`${t?.documents?.region_sds}`}</span>
                    </div>
                  </th>
                ): <></>}
              </tr>
            </thead>
            <tbody>
            {currentDocuments.map((item, index) => (
              <Fragment key={`resources-section--${item.id}--${index}`}>
                {item?.field_media_file?.uri?.url && (
                  <tr className="even:bg-hygienaLight odd:bg-white">
                    <td className="px-2 py-3 lg:min-w-[400px] pl-6">
                      <div className="flex gap-4 items-center">
                        {item?.field_media_file?.uri?.url && (
                          <>
                            <Button href={getDocumentUri(item) ?? ""} hover={{color: "primary"}} icon={"file-search"} iconClass="!py-0 !mr-0" className="!p-1.5" target={"_blank"} height={25} width={25}/>
                            <Link href={getDocumentUri(item) ?? "/"} className="hover:text-primary transition-all duration-300" target="_blank" {...docLinkProps}>{item.name}</Link>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="text-primary px-2 py-3 cursor-pointer max-md:hidden">
                      {item?.field_document_categories && item.field_document_categories.map((term, index) => {
                        if (index < (item.field_document_categories?.length - 1)) {
                          return (<span key={`${item.id}--${term.id}`} onClick={() => {setCategory(term.id); setPage(0)}}>{`${term.name}, `}</span>)
                        }
                        return <span key={`${item.id}--${term.id}`} onClick={() => {setCategory(term.id); setPage(0)}}>{term.name}</span>
                      })}
                    </td>
                    {languageFilters?.length ? (
                      <td className="text-primary px-2 py-3 break-all cursor-pointer max-md:hidden" onClick={() => {setLanguage(item?.field_document_language?.[0]?.id ?? ""); setPage(0);}}>
                        {item?.field_document_language?.[0] && item?.field_document_language?.[0]?.name}
                      </td>
                    ): <></>}
                    {regionFilters?.length ? (
                      <td className="text-primary px-2 py-3 break-all cursor-pointer max-md:hidden" onClick={() => {setRegion(item?.field_document_region?.[0]?.id ?? ""); setPage(0);}}>
                        {item?.field_document_region?.[0] && item?.field_document_region?.[0]?.name}
                      </td>
                    ): <></>}
                  </tr>
                )}
              </Fragment>
            ))}
            </tbody>
          </table>
        </div>
        <div className="flex flex-col md:flex-row gap-6 justify-left items-center px-6 bg-white py-6 justify-between">
          <label htmlFor="document-search--per-page" className="hidden md:inline">
            {`${t?.documents?.show}`}
            <select id="document-search--per-page" name="per_page"
                    onChange={(event) => {
                      setPage(0);
                      setPageLimit(parseInt(event.target.value));
                    }}
                    value={pageLimit}
                    className="px-2 py-1 border border-black rounded-sm mx-2">
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
            {`${t?.documents?.per_page}`}
          </label>
          <div className="max-md:self-baseline lowercase">{documentCount} {documentCount > 1 || documentCount === 0 ? `${t?.documents?.documents}` : `${t?.documents?.document}`}</div>
          <DocumentPagination count={documentCount} page={page} limit={pageLimit} changePage={changePage} radius={8}/>
        </div>
      </div>
    </>
  )
}
